/**
 * SmartStore Smart SQL Query Utilities
 *
 * This module provides utilities for converting SOQL queries to SmartStore Smart SQL format
 * for offline data access on iOS devices using Salesforce Mobile SDK.
 */

// SQL keywords that should NOT be converted to SmartStore field references
const SQL_KEYWORDS = new Set([
  'SELECT',
  'FROM',
  'WHERE',
  'AND',
  'OR',
  'NOT',
  'IN',
  'LIKE',
  'ORDER',
  'BY',
  'GROUP',
  'HAVING',
  'LIMIT',
  'OFFSET',
  'ASC',
  'DESC',
  'NULLS',
  'FIRST',
  'LAST',
  'NULL',
  'TRUE',
  'FALSE',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'COUNT_DISTINCT',
  'CALENDAR_MONTH',
  'CALENDAR_QUARTER',
  'CALENDAR_YEAR',
  'DAY_IN_MONTH',
  'DAY_IN_WEEK',
  'DAY_IN_YEAR',
  'DAY_ONLY',
  'FISCAL_MONTH',
  'FISCAL_QUARTER',
  'FISCAL_YEAR',
  'HOUR_IN_DAY',
  'WEEK_IN_MONTH',
  'WEEK_IN_YEAR',
  'INCLUDES',
  'EXCLUDES',
  'ABOVE',
  'BELOW',
  'ABOVE_OR_BELOW',
  'AT',
  'TYPEOF',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'USING',
  'SCOPE',
  'DATA',
  'CATEGORY',
  'ROLLUP',
  'CUBE',
  'FORMAT',
  'CONVERTCURRENCY',
  'TOLABEL',
  'DISTANCE',
  'GEOLOCATION',
])

// Aggregate functions that need special handling
const AGGREGATE_FUNCTIONS = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT_DISTINCT']

/**
 * Checks if a string is a SQL keyword
 */
function isSqlKeyword(word: string): boolean {
  return SQL_KEYWORDS.has(word.toUpperCase())
}

/**
 * Checks if a value is a literal (string, number, boolean, null, date)
 */
function isLiteralValue(value: string): boolean {
  const trimmed = value.trim()

  // Check for quoted strings
  if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
    return true
  }

  // Check for numbers
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return true
  }

  // Check for boolean
  if (/^(true|false)$/i.test(trimmed)) {
    return true
  }

  // Check for null
  if (/^null$/i.test(trimmed)) {
    return true
  }

  // Check for date/datetime literals
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/.test(trimmed)) {
    return true
  }

  // Check for date literals like TODAY, YESTERDAY, LAST_N_DAYS:n, etc.
  if (
    /^(TODAY|YESTERDAY|TOMORROW|LAST_WEEK|THIS_WEEK|NEXT_WEEK|LAST_MONTH|THIS_MONTH|NEXT_MONTH|LAST_90_DAYS|NEXT_90_DAYS|LAST_N_DAYS:\d+|NEXT_N_DAYS:\d+|LAST_N_WEEKS:\d+|NEXT_N_WEEKS:\d+|LAST_N_MONTHS:\d+|NEXT_N_MONTHS:\d+|LAST_N_QUARTERS:\d+|NEXT_N_QUARTERS:\d+|LAST_N_YEARS:\d+|NEXT_N_YEARS:\d+|THIS_QUARTER|LAST_QUARTER|NEXT_QUARTER|THIS_YEAR|LAST_YEAR|NEXT_YEAR|THIS_FISCAL_QUARTER|LAST_FISCAL_QUARTER|NEXT_FISCAL_QUARTER|THIS_FISCAL_YEAR|LAST_FISCAL_YEAR|NEXT_FISCAL_YEAR)$/i.test(
      trimmed,
    )
  ) {
    return true
  }

  return false
}

/**
 * Checks if a token is an ISO 8601 datetime literal (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.sssZ)
 */
function isDateTimeLiteral(token: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/.test(token)
}

/**
 * Converts a field reference to SmartStore syntax
 * Handles relationship fields (Account.Name) and simple fields (Name)
 *
 * In SmartStore, relationship fields are stored as denormalized nested data within the soup.
 * So `Child_Account_vod__r.Name` becomes `{Child_Account_vod__c:Child_Account_vod__r.Name}`
 * - the full relationship path is preserved within the primary object's soup.
 */
function convertFieldToSmartStore(field: string, primaryObject: string, objectAliases: Map<string, string>): string {
  const trimmed = field.trim()

  // Skip if it's a keyword or literal
  if (isSqlKeyword(trimmed) || isLiteralValue(trimmed)) {
    return trimmed
  }

  // Handle FIELDS(ALL) placeholder - convert to SmartStore _soup syntax
  // SmartStore uses {ObjectName:_soup} to select all fields from a soup
  if (trimmed === '__FIELDS_ALL_PLACEHOLDER__') {
    return `{${primaryObject}:_soup}`
  }

  // Handle wildcard * - should not be wrapped in SmartStore field reference syntax
  if (trimmed === '*') {
    return '*'
  }

  // Check if already in SmartStore format
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    return trimmed
  }

  // Handle relationship fields (e.g., Account.Name, Child_Account_vod__r.Name)
  // In SmartStore, relationship data is stored denormalized within the soup,
  // so we keep the full path and use the primary object as the soup name
  if (trimmed.includes('.')) {
    // Check if the first part is an alias for the primary object
    const parts = trimmed.split('.')
    const alias = parts[0]
    if (objectAliases.has(alias.toLowerCase()) && objectAliases.get(alias.toLowerCase()) === primaryObject) {
      // It's an alias for the primary object, use the rest as field path
      const fieldPath = parts.slice(1).join('.')
      return `{${primaryObject}:${fieldPath}}`
    }
    // For relationship fields, keep the full path within the primary object's soup
    return `{${primaryObject}:${trimmed}}`
  }

  // Simple field - use primary object
  return `{${primaryObject}:${trimmed}}`
}

/**
 * Converts an aggregate function to SmartStore syntax
 * e.g., COUNT(Id) → COUNT({Account:Id})
 */
function convertAggregateFunction(expr: string, primaryObject: string, objectAliases: Map<string, string>): string {
  for (const func of AGGREGATE_FUNCTIONS) {
    const regex = new RegExp(`\\b${func}\\s*\\(([^)]+)\\)`, 'gi')
    expr = expr.replace(regex, (_match, innerField) => {
      const trimmedInner = innerField.trim()
      // Handle COUNT(*)
      if (trimmedInner === '*') {
        return `${func}(*)`
      }
      // Handle DISTINCT
      if (trimmedInner.toUpperCase().startsWith('DISTINCT ')) {
        const fieldPart = trimmedInner.substring(9).trim()
        const smartField = convertFieldToSmartStore(fieldPart, primaryObject, objectAliases)
        return `${func}(DISTINCT ${smartField})`
      }
      const smartField = convertFieldToSmartStore(trimmedInner, primaryObject, objectAliases)
      return `${func}(${smartField})`
    })
  }
  return expr
}

/**
 * Parses the SELECT clause fields, handling aggregate functions and aliases
 */
function parseSelectFields(selectClause: string): string[] {
  const fields: string[] = []
  let depth = 0
  let currentField = ''

  for (let i = 0; i < selectClause.length; i++) {
    const char = selectClause[i]

    if (char === '(') {
      depth++
      currentField += char
    } else if (char === ')') {
      depth--
      currentField += char
    } else if (char === ',' && depth === 0) {
      if (currentField.trim()) {
        fields.push(currentField.trim())
      }
      currentField = ''
    } else {
      currentField += char
    }
  }

  if (currentField.trim()) {
    fields.push(currentField.trim())
  }

  return fields
}

/**
 * Converts SELECT fields to SmartStore syntax
 */
function convertSelectFields(fields: string[], primaryObject: string, objectAliases: Map<string, string>): string[] {
  return fields.map((field) => {
    // Check for alias (e.g., "COUNT(Id) cnt" or "Name AS n")
    const aliasMatch = field.match(/^(.+?)\s+(?:AS\s+)?([A-Za-z_][A-Za-z0-9_]*)$/i)
    let expression = field
    let alias = ''

    if (aliasMatch) {
      expression = aliasMatch[1].trim()
      alias = aliasMatch[2]
    }

    // Check if it's an aggregate function
    const hasAggregate = AGGREGATE_FUNCTIONS.some((func) => new RegExp(`\\b${func}\\s*\\(`, 'i').test(expression))

    if (hasAggregate) {
      const converted = convertAggregateFunction(expression, primaryObject, objectAliases)
      return alias ? `${converted} ${alias}` : converted
    }

    // Regular field
    const converted = convertFieldToSmartStore(expression, primaryObject, objectAliases)
    return alias ? `${converted} ${alias}` : converted
  })
}

/**
 * Converts SOQL null comparisons to SmartStore (SQL) syntax
 * SOQL: = null, != null
 * SQL:  IS NULL, IS NOT NULL
 *
 * IMPORTANT: Process != and <> BEFORE = to avoid partial matches
 */
function convertNullComparisons(clause: string): string {
  // Convert "!= null" or "!=null" to "IS NOT NULL" (case insensitive) - MUST be before = null
  let result = clause.replace(/\s*!=\s*null\b/gi, ' IS NOT NULL')

  // Convert "<> null" to "IS NOT NULL" (case insensitive)
  result = result.replace(/\s*<>\s*null\b/gi, ' IS NOT NULL')

  // Convert "= null" or "=null" to "IS NULL" (case insensitive) - MUST be after != null
  result = result.replace(/\s*=\s*null\b/gi, ' IS NULL')

  return result
}

/**
 * Converts WHERE clause to SmartStore syntax
 * Preserves operators, literals, and keywords while converting field references
 * Also converts SOQL null comparisons (= null, != null) to SQL syntax (IS NULL, IS NOT NULL)
 */
function convertWhereClause(whereClause: string, primaryObject: string, objectAliases: Map<string, string>): string {
  // Tokenize the WHERE clause while preserving structure
  let result = ''
  let i = 0
  const len = whereClause.length

  while (i < len) {
    const char = whereClause[i]

    // Skip whitespace
    if (/\s/.test(char)) {
      result += char
      i++
      continue
    }

    // Handle quoted strings - pass through unchanged
    if (char === "'" || char === '"') {
      const quote = char
      result += char
      i++
      while (i < len && whereClause[i] !== quote) {
        // Handle escaped quotes
        if (whereClause[i] === '\\' && i + 1 < len) {
          result += whereClause[i] + whereClause[i + 1]
          i += 2
        } else {
          result += whereClause[i]
          i++
        }
      }
      if (i < len) {
        result += whereClause[i]
        i++
      }
      continue
    }

    // Handle operators and special characters
    if (/[()=<>!,]/.test(char)) {
      result += char
      i++
      // Handle multi-char operators
      if ((char === '!' || char === '<' || char === '>') && i < len && whereClause[i] === '=') {
        result += whereClause[i]
        i++
      }
      continue
    }

    // Handle identifiers and keywords
    if (/[a-zA-Z_]/.test(char)) {
      let identifier = ''
      while (i < len && /[a-zA-Z0-9_.]/.test(whereClause[i])) {
        identifier += whereClause[i]
        i++
      }

      // Check if it's a keyword, literal, or field
      if (isSqlKeyword(identifier) || isLiteralValue(identifier)) {
        result += identifier
      } else {
        // It's a field reference
        result += convertFieldToSmartStore(identifier, primaryObject, objectAliases)
      }
      continue
    }

    // Handle numbers and datetime literals
    if (/[0-9-]/.test(char)) {
      let token = ''

      // Handle negative numbers or dates starting with digit
      if (char === '-') {
        token += char
        i++
      }

      // Read digits, dots, colons, and dashes for dates/datetimes
      while (i < len && /[0-9.:\-TZ+]/.test(whereClause[i])) {
        token += whereClause[i]
        i++
      }

      // Wrap datetime literals in single quotes for Smart SQL (SQLite requirement)
      if (isDateTimeLiteral(token)) {
        result += `'${token}'`
      } else {
        result += token
      }
      continue
    }

    // Pass through any other character
    result += char
    i++
  }

  // Convert SOQL null comparisons to SQL syntax
  result = convertNullComparisons(result)

  return result
}

/**
 * Parses ORDER BY clause into field and direction pairs
 */
function parseOrderByClause(orderByClause: string): Array<{ field: string; direction: string }> {
  const result: Array<{ field: string; direction: string }> = []

  // Split by comma, but be careful with functions
  const parts = orderByClause.split(/,(?![^()]*\))/)

  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed) continue

    // Match field and optional direction (ASC/DESC) and NULLS FIRST/LAST
    const match = trimmed.match(/^(.+?)\s*(ASC|DESC)?\s*(NULLS\s+(?:FIRST|LAST))?$/i)

    if (match) {
      const field = match[1].trim()
      let direction = match[2] ? match[2].toUpperCase() : ''
      if (match[3]) {
        direction += (direction ? ' ' : '') + match[3].toUpperCase()
      }
      result.push({ field, direction })
    } else {
      result.push({ field: trimmed, direction: '' })
    }
  }

  return result
}

/**
 * Converts ORDER BY fields to SmartStore syntax
 */
function convertOrderByFields(
  orderByParts: Array<{ field: string; direction: string }>,
  primaryObject: string,
  objectAliases: Map<string, string>,
): string[] {
  return orderByParts.map(({ field, direction }) => {
    // Check for aggregate functions
    const hasAggregate = AGGREGATE_FUNCTIONS.some((func) => new RegExp(`\\b${func}\\s*\\(`, 'i').test(field))

    let convertedField: string
    if (hasAggregate) {
      convertedField = convertAggregateFunction(field, primaryObject, objectAliases)
    } else {
      convertedField = convertFieldToSmartStore(field, primaryObject, objectAliases)
    }

    return direction ? `${convertedField} ${direction}` : convertedField
  })
}

/**
 * Parses GROUP BY clause fields
 */
function parseGroupByClause(groupByClause: string): string[] {
  return groupByClause
    .split(/,(?![^()]*\))/)
    .map((f) => f.trim())
    .filter(Boolean)
}

/**
 * Converts GROUP BY fields to SmartStore syntax
 */
function convertGroupByFields(fields: string[], primaryObject: string, objectAliases: Map<string, string>): string[] {
  return fields.map((field) => {
    // Check for aggregate functions (ROLLUP, CUBE)
    if (/^(ROLLUP|CUBE)\s*\(/i.test(field)) {
      return field.replace(/\(([^)]+)\)/g, (_match, inner) => {
        const innerFields = inner.split(',').map((f: string) => f.trim())
        const converted = innerFields.map((f: string) => convertFieldToSmartStore(f, primaryObject, objectAliases))
        return `(${converted.join(', ')})`
      })
    }
    return convertFieldToSmartStore(field, primaryObject, objectAliases)
  })
}

/**
 * Extracts FROM clause and any JOINs
 * Returns the primary object and any joined objects
 */
function parseFromClause(fromClause: string): {
  primary: string
  alias?: string
  joins: Array<{ object: string; alias?: string; type: string; on?: string }>
} {
  const joins: Array<{ object: string; alias?: string; type: string; on?: string }> = []

  // Match primary object with optional alias
  const primaryMatch = fromClause.match(/^([A-Za-z0-9_]+)(?:\s+(?:AS\s+)?([A-Za-z_][A-Za-z0-9_]*))?/i)

  if (!primaryMatch) {
    return { primary: '', joins }
  }

  const primary = primaryMatch[1]
  const alias = primaryMatch[2]

  // Match JOIN clauses
  const joinRegex =
    /(LEFT\s+OUTER\s+JOIN|LEFT\s+JOIN|INNER\s+JOIN|JOIN)\s+([A-Za-z0-9_]+)(?:\s+(?:AS\s+)?([A-Za-z_][A-Za-z0-9_]*))?\s+ON\s+([^,]+?)(?=(?:LEFT|INNER|JOIN|$))/gi
  let joinMatch

  while ((joinMatch = joinRegex.exec(fromClause)) !== null) {
    joins.push({
      type: joinMatch[1].toUpperCase().replace(/\s+/g, ' '),
      object: joinMatch[2],
      alias: joinMatch[3],
      on: joinMatch[4]?.trim(),
    })
  }

  return { primary, alias, joins }
}

/**
 * Comprehensive SOQL to SmartStore Smart Query converter
 *
 * Converts standard SOQL queries to SmartStore Smart SQL format for offline use on iOS.
 * Handles SELECT, FROM, WHERE, ORDER BY, GROUP BY, HAVING, LIMIT, OFFSET,
 * aggregate functions, and relationship fields.
 *
 * SmartStore stores denormalized data, so relationship fields are accessed directly
 * within the soup using the full relationship path (e.g., Child_Account_vod__r.Name).
 *
 * @param soqlQuery - The SOQL query string to convert
 * @returns The converted SmartStore Smart SQL query string
 *
 * @example
 * // Simple query
 * convertSoqlToSmartQuery('SELECT Id, Name FROM Account WHERE Active = true')
 * // Returns: SELECT {Account:Id}, {Account:Name} FROM {Account} WHERE {Account:Active} = true
 *
 * @example
 * // With relationship fields (denormalized access - no joins needed)
 * convertSoqlToSmartQuery('SELECT Id, Child_Account_vod__r.Name FROM Child_Account_vod__c')
 * // Returns: SELECT {Child_Account_vod__c:Id}, {Child_Account_vod__c:Child_Account_vod__r.Name} FROM {Child_Account_vod__c}
 *
 * @example
 * // With aggregate functions
 * convertSoqlToSmartQuery('SELECT COUNT(Id), Industry FROM Account GROUP BY Industry')
 * // Returns: SELECT COUNT({Account:Id}), {Account:Industry} FROM {Account} GROUP BY {Account:Industry}
 *
 * @example
 * // With ORDER BY and LIMIT
 * convertSoqlToSmartQuery('SELECT Id, Name FROM Contact ORDER BY LastName ASC, FirstName DESC LIMIT 100')
 * // Returns: SELECT {Contact:Id}, {Contact:Name} FROM {Contact} ORDER BY {Contact:LastName} ASC, {Contact:FirstName} DESC LIMIT 100
 */
export function convertSoqlToSmartQuery(soqlQuery: string): string {
  if (!soqlQuery || typeof soqlQuery !== 'string') {
    return ''
  }

  // Normalize whitespace
  let normalizedQuery = soqlQuery.replace(/\s+/g, ' ').trim()

  // Extract FROM clause first to get the object name for FIELDS() conversion
  const fromMatch = normalizedQuery.match(
    /FROM\s+([\s\S]+?)(?=\s+WHERE\s+|\s+ORDER\s+BY\s+|\s+GROUP\s+BY\s+|\s+HAVING\s+|\s+LIMIT\s+|\s+OFFSET\s+|$)/i,
  )
  if (!fromMatch) {
    return soqlQuery
  }

  // Parse FROM clause to get primary object name
  const fromParsed = parseFromClause(fromMatch[1].trim())
  const primaryObject = fromParsed.primary

  if (!primaryObject) {
    return soqlQuery
  }

  // Replace SOQL-specific FIELDS() syntax with __FIELDS_ALL_PLACEHOLDER__
  // We'll replace this with {ObjectName:_soup} after we know the object name
  // FIELDS(ALL), FIELDS(STANDARD), FIELDS(CUSTOM) are SOQL-specific and not supported by SmartStore,
  // uses {ObjectName:_soup} to select all fields from a soup
  normalizedQuery = normalizedQuery.replace(
    /\bFIELDS\s*\(\s*(ALL|STANDARD|CUSTOM)\s*\)/gi,
    '__FIELDS_ALL_PLACEHOLDER__',
  )

  // Extract SELECT clause
  const selectMatch = normalizedQuery.match(/SELECT\s+([\s\S]+?)\s+FROM\s+/i)
  if (!selectMatch) {
    return soqlQuery
  }

  // Extract other clauses
  const whereMatch = normalizedQuery.match(
    /WHERE\s+([\s\S]+?)(?=\s+ORDER\s+BY\s+|\s+GROUP\s+BY\s+|\s+HAVING\s+|\s+LIMIT\s+|\s+OFFSET\s+|$)/i,
  )
  const groupByMatch = normalizedQuery.match(
    /GROUP\s+BY\s+([\s\S]+?)(?=\s+HAVING\s+|\s+ORDER\s+BY\s+|\s+LIMIT\s+|\s+OFFSET\s+|$)/i,
  )
  const havingMatch = normalizedQuery.match(/HAVING\s+([\s\S]+?)(?=\s+ORDER\s+BY\s+|\s+LIMIT\s+|\s+OFFSET\s+|$)/i)
  const orderByMatch = normalizedQuery.match(/ORDER\s+BY\s+([\s\S]+?)(?=\s+LIMIT\s+|\s+OFFSET\s+|$)/i)
  const limitMatch = normalizedQuery.match(/LIMIT\s+(\d+)/i)
  const offsetMatch = normalizedQuery.match(/OFFSET\s+(\d+)/i)

  // Build alias map for the primary object
  // SmartStore stores denormalized data, so relationship fields are accessed
  // directly within the soup without needing joins
  const objectAliases = new Map<string, string>()
  if (fromParsed.alias) {
    objectAliases.set(fromParsed.alias.toLowerCase(), primaryObject)
  }
  objectAliases.set(primaryObject.toLowerCase(), primaryObject)

  // Build the Smart Query
  const parts: string[] = []

  // SELECT clause
  const selectFields = parseSelectFields(selectMatch[1].trim())
  const smartSelectFields = convertSelectFields(selectFields, primaryObject, objectAliases)
  parts.push(`SELECT ${smartSelectFields.join(', ')}`)

  // FROM clause - just the primary object (SmartStore stores denormalized data)
  parts.push(`FROM {${primaryObject}}`)

  // WHERE clause
  const whereConditions: string[] = []

  // Add original WHERE conditions
  if (whereMatch) {
    const smartWhere = convertWhereClause(whereMatch[1].trim(), primaryObject, objectAliases)
    whereConditions.push(smartWhere)
  }

  // Combine all WHERE conditions
  if (whereConditions.length > 0) {
    parts.push(`WHERE ${whereConditions.join(' AND ')}`)
  }

  // GROUP BY clause
  if (groupByMatch) {
    const groupByFields = parseGroupByClause(groupByMatch[1].trim())
    const smartGroupBy = convertGroupByFields(groupByFields, primaryObject, objectAliases)
    parts.push(`GROUP BY ${smartGroupBy.join(', ')}`)
  }

  // HAVING clause
  if (havingMatch) {
    const smartHaving = convertWhereClause(havingMatch[1].trim(), primaryObject, objectAliases)
    parts.push(`HAVING ${smartHaving}`)
  }

  // ORDER BY clause
  if (orderByMatch) {
    const orderByParts = parseOrderByClause(orderByMatch[1].trim())
    const smartOrderBy = convertOrderByFields(orderByParts, primaryObject, objectAliases)
    parts.push(`ORDER BY ${smartOrderBy.join(', ')}`)
  }

  // LIMIT clause
  if (limitMatch) {
    parts.push(`LIMIT ${limitMatch[1]}`)
  }

  // OFFSET clause
  if (offsetMatch) {
    parts.push(`OFFSET ${offsetMatch[1]}`)
  }

  return parts.join(' ')
}
